import type {
  EligibilityStatusSignature,
  LimitsStatusDataSignature,
} from '../services/tuition-assistance.ts';
import { formatAsDollars, toLocaleDateString } from './format.ts';

// ---------------------------------------------------------------------------
// Blurb generation for the Approval Decision Banner
//
// Pure functions — no side effects, no component state.
// These produce human-readable paragraph summaries from the raw API data.
// ---------------------------------------------------------------------------

// Shared label maps — exported for reuse by GUI components
export const LOCK_BASIS_LABELS: Record<string, string> = {
  COURSE_START_DATE: 'course start date',
  FULFILLED_DATE: 'fulfillment date',
  ON_COURSES_SUBMISSION: 'courses submission date',
  APPROVED_DATE: 'approval date',
};

export const PARTICIPANT_STATE_LABELS: Record<string, string> = {
  'TAS.ParticipantState.ACTIVE': 'Active',
  'TAS.ParticipantState.ELIGIBLE': 'Eligible',
  'TAS.ParticipantState.HELD': 'Held',
  'TAS.ParticipantState.INACTIVE': 'Inactive',
  'TAS.ParticipantState.TERMINATED': 'Terminated',
};

export const LIMIT_LEVEL_LABELS: Record<string, string> = {
  company_annual: 'Company (Annual)',
  company: 'Company',
  product_annual: 'Product (Annual)',
  product: 'Product',
  batch_annual: 'Batch (Annual)',
  batch: 'Batch',
  scheme_annual: 'Program (Annual)',
  scheme: 'Program',
  none: 'None',
};

// Lowercase variants for prose blurbs
const LIMIT_LEVEL_PROSE_LABELS: Record<string, string> = {
  company_annual: 'company annual limit',
  company: 'company limit',
  product_annual: 'product annual limit',
  product: 'product limit',
  batch_annual: 'batch annual limit',
  batch: 'batch limit',
  scheme_annual: 'program annual limit',
  scheme: 'program limit',
  none: 'no limit',
};

export const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  FULL_TIME: 'Full-time',
  PART_TIME: 'Part-time',
  CONTRACT: 'Contract',
  TEMPORARY: 'Temporary',
  INTERN: 'Intern',
  SEASONAL: 'Seasonal',
};

/**
 * Generates a human-readable paragraph explaining the eligibility determination.
 *
 * Follows the template logic from the "Eligibility LLM Instruction" document:
 * - Scenario A: Admin override (ignore_policies = true)
 * - Scenario B: Standard eligibility logic with lock basis, participant state, employment
 */
export function generateEligibilityBlurb(data: EligibilityStatusSignature): string {
  // Scenario A: Admin override
  if (data.ignore_policies) {
    return (
      'This application has policy enforcement disabled (Admin Override). ' +
      'Eligibility rules are bypassed — the employee will be paid regardless of ' +
      'participant status or employment state.'
    );
  }

  const parts: string[] = [];

  // Lock basis context
  const basisLabel =
    LOCK_BASIS_LABELS[data.lock_basis] ?? data.lock_basis?.replace(/_/g, ' ').toLowerCase();
  const stateLabel = PARTICIPANT_STATE_LABELS[data.participant_state ?? ''] ?? 'Unknown';

  if (data.lock_date) {
    parts.push(
      `Employee eligibility was locked at the ${basisLabel} (${toLocaleDateString(data.lock_date)}). ` +
        `At that point, the participant status was ${stateLabel}.`
    );
  } else {
    parts.push(
      `The lock basis for this program is ${basisLabel}, but the lock date has not yet been determined. ` +
        `The current participant status is ${stateLabel}.`
    );
  }

  // Employment context
  const empType = data.employment_type;
  if (empType) {
    const empLabel = (EMPLOYMENT_TYPE_LABELS[empType] ?? empType.replace(/_/g, ' ')).toLowerCase();
    parts.push(`The employee is classified as ${empLabel}.`);
  }

  if (data.deactivated_on) {
    if (data.pay_deactivated_employees) {
      parts.push(
        `The employee was deactivated on ${toLocaleDateString(data.deactivated_on)}, however the company's policy ` +
          `allows payment to deactivated employees — this does not block eligibility.`
      );
    } else {
      parts.push(
        `The employee was deactivated on ${toLocaleDateString(data.deactivated_on)} and the company does not pay ` +
          `deactivated employees. This makes the employee ineligible for payment.`
      );
    }
  }

  // Final determination
  if (data.is_eligible) {
    parts.push('Based on these factors, this employee is eligible for payment.');
  } else {
    const reasons: string[] = [];
    const state = data.participant_state;
    if (state !== 'TAS.ParticipantState.ACTIVE' && state !== 'TAS.ParticipantState.ELIGIBLE') {
      reasons.push(`participant status is ${stateLabel}`);
    }
    if (data.deactivated_on && !data.pay_deactivated_employees) {
      reasons.push('employee is deactivated without a pay-deactivated policy');
    }
    const reasonText = reasons.length > 0 ? ` because ${reasons.join(' and ')}` : '';
    parts.push(`Based on these factors, this employee is not eligible for payment${reasonText}.`);
  }

  return parts.join(' ');
}

/**
 * Generates a plain-English summary of the limits/benefit situation.
 *
 * Based on the "Standard Response Template (Human Readable)" from the
 * Limits API v2 documentation.
 */
export function generateLimitsSummary(data: LimitsStatusDataSignature): string {
  const parts: string[] = [];
  const isCredits = data.limit_capacity === 'TAS.LimitCapacity.CREDITS';

  const formatAmount = (cents: number) => {
    if (isCredits) return `${cents} credit${cents === 1 ? '' : 's'}`;
    return formatAsDollars(cents);
  };

  // Date context
  const asOf = data.as_of_date ? `As of ${toLocaleDateString(data.as_of_date)}, this` : 'This';

  // Available amount + constraining level (follows documented template)
  if (data.available !== null && data.available !== undefined) {
    const softLabel = LIMIT_LEVEL_PROSE_LABELS[data.soft_limit] ?? data.soft_limit;

    if (data.soft_limit !== 'none') {
      // Extract the constraining scope's limit and usage
      const scopeKey = data.soft_limit.replace(/_annual$/, '') as keyof typeof data.details;
      const scopeDetail = data.details?.[scopeKey];

      let scopeContext = '';
      if (scopeDetail) {
        const scopeLimit = scopeDetail.annual_limit;
        const scopeUsage = scopeDetail.annual_usage;
        if (scopeLimit != null) {
          if (scopeUsage === 0) {
            const availableNoun = isCredits
              ? 'all credits are available'
              : 'all funds are available';
            scopeContext = ` The ${softLabel} allows up to ${formatAmount(scopeLimit)} and ${availableNoun} for the current period.`;
          } else {
            scopeContext =
              ` While the ${softLabel} allows up to ${formatAmount(scopeLimit)}, ` +
              `${formatAmount(scopeUsage)} has already been utilized during the current period.`;
          }
        }
      }

      parts.push(
        `${asOf} employee has ${formatAmount(data.available)} available. ` +
          `The ${softLabel} is the primary constraint.${scopeContext}`
      );
    } else {
      parts.push(`${asOf} employee has ${formatAmount(data.available)} in benefit remaining.`);
    }
  } else {
    parts.push(`${asOf} employee has no benefit limit (unlimited coverage).`);
  }

  // Limit context
  if (data.hard_limit !== 'none') {
    const hardLabel = LIMIT_LEVEL_PROSE_LABELS[data.hard_limit] ?? data.hard_limit;
    const isHard = data.soft_limit === data.hard_limit;
    if (isHard) {
      parts.push('This is considered a limit.');
    } else {
      parts.push(`The ${hardLabel} is enforced as a cap.`);
    }
  } else if (data.soft_limit !== 'none') {
    parts.push('This is considered a soft limit.');
  }

  // Period
  if (data.annual_start_date && data.annual_end_date) {
    parts.push(
      `The benefit year runs from ${toLocaleDateString(data.annual_start_date)} to ${toLocaleDateString(data.annual_end_date)}.`
    );
  }

  return parts.join(' ');
}
